import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { getAccessToken } from "../../awsAuth";
import { useSubscription } from "../../contexts/SubscriptionContext";
import CreatePost from "./CreatePost";
import CreateSearch from "./CreateSearch";
import CategoryFields from "./CategoryFields";
import { categoryVisualMap } from "./categoryVisualMap";
import { keywordCategoryMap } from "./keywordCategoryMap";
import { compressImage } from "../../utils/imageCompress";
import { debounce } from "lodash";
import "./CreateListing.scss";

import listingIcon from "./img/listing.png";
import postIcon from "./img/post.png";
import searchIcon from "./img/search.png";
import addImageIcon from "./img/add-image.png";

import DynamicHeader from "../Header/DynamicHeader";

import { FuzzyCategoryMatcher } from "./engine/fuzzyCategoryMatcher";
import { classifyCategory } from "./engine/categoryClassifier";
import { collectAttributes } from "../../utils/attributes";

import { useNavigate, useSearchParams } from "react-router-dom";
import SuccessModal from "./SuccessModal";

import DraftResumePrompt from "./DraftResumePrompt";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const categoryMatcher = new FuzzyCategoryMatcher(keywordCategoryMap, {
  maxEditDistance: 2,
  minScore: 0.52,
  enableTranslit: true,
});

const NO_CONDITION_CATS = new Set([
  "Послуги",
  "Тварини",
  "Робота",
  "Освіта",
  "Оренда та прокат",
]);
const isConditionRelevant = (cat) => !NO_CONDITION_CATS.has(cat || "");

/* ===== Draft (localStorage) ===== */
const LISTING_DRAFT_LS_KEY = "cvanto:draft:createListing:v1";
const safeJsonParse = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};
const nowIso = () => new Date().toISOString();

const normalizeWords = (text = "") =>
  String(text)
    .toLowerCase()
    .replace(/[^\wА-Яа-яІіЇїЄєҐґ'’\s-]/g, " ")
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean);

const detectAutoCategory = (text) => {
  const clean = String(text || "").trim();
  if (!clean) return "";

  const words = normalizeWords(clean);

  for (const word of words) {
    if (keywordCategoryMap[word]) {
      return keywordCategoryMap[word];
    }
  }

  const smart = classifyCategory(clean);
  if (smart?.category) {
    return smart.category;
  }

  const top = categoryMatcher.detectCategories(clean, 1)?.[0];
  if (top?.category) {
    return top.category;
  }

  return "";
};

const CreateListing = () => {
  const { userId } = useAuth();
  const { isActive } = useSubscription();
  const maxPhotos = 6;

  const [view, setView] = useState("listing");
  const [showCategories, setShowCategories] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [isCategoryManuallySelected, setIsCategoryManuallySelected] =
    useState(false);

  const [photos, setPhotos] = useState([]);

  const [formData, setFormData] = useState({ attributes: {} });

  const [transferType, setTransferType] = useState("sale");
  const [priceData, setPriceData] = useState({ currency: "ГРН" });

  const [userProfile, setUserProfile] = useState(null);
  const [location, setLocation] = useState("");

  const [uploading, setUploading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState(0);
  const [publishPulse, setPublishPulse] = useState(".");
  const [message, setMessage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  const [debouncedTitle, setDebouncedTitle] = useState("");
  const [debouncedDescription, setDebouncedDescription] = useState("");

  const fileInputRef = useRef(null);
  const selectRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  const [showPhone, setShowPhone] = useState(true);
  const [contactPhone, setContactPhone] = useState("");

  const [condition, setCondition] = useState("na");

  //Торг
  const [isNegotiable, setIsNegotiable] = useState(false);

  const titleRef = useRef(null);
  const categoryTriggerRef = useRef(null);
  const photosGridRef = useRef(null);
  const priceRef = useRef(null);
  const exchangeRef = useRef(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isBusinessVacancyMode =
    searchParams.get("businessModule") === "vacancies";

  const [showSuccess, setShowSuccess] = useState(false);

  /* ===== Draft prompt state ===== */
  const [draftCandidate, setDraftCandidate] = useState(null);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const draftHydratedRef = useRef(false);
  const suppressDraftWriteRef = useRef(false);

  /* ===== Geolocation (added) ===== */
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState("");
  const lastGeoRef = useRef(null);

  const publishProgressTimerRef = useRef(null);
  const publishPulseTimerRef = useRef(null);

  const isBusy = uploading || isPublishing;

  const startPublishingVisualState = useCallback(() => {
    setIsPublishing(true);
    setPublishProgress(8);
    setPublishPulse(".");

    if (publishProgressTimerRef.current) {
      clearInterval(publishProgressTimerRef.current);
    }
    if (publishPulseTimerRef.current) {
      clearInterval(publishPulseTimerRef.current);
    }

    publishProgressTimerRef.current = setInterval(() => {
      setPublishProgress((prev) => {
        if (prev >= 92) return prev;
        const nextStep =
          prev < 30 ? 7 : prev < 55 ? 5 : prev < 75 ? 3 : prev < 90 ? 2 : 1;
        return Math.min(prev + nextStep, 92);
      });
    }, 220);

    publishPulseTimerRef.current = setInterval(() => {
      setPublishPulse((prev) => {
        if (prev === ".") return "..";
        if (prev === "..") return "...";
        return ".";
      });
    }, 380);
  }, []);

  const stopPublishingVisualState = useCallback((done = false) => {
    if (publishProgressTimerRef.current) {
      clearInterval(publishProgressTimerRef.current);
      publishProgressTimerRef.current = null;
    }
    if (publishPulseTimerRef.current) {
      clearInterval(publishPulseTimerRef.current);
      publishPulseTimerRef.current = null;
    }

    if (done) {
      setPublishProgress(100);
      setTimeout(() => {
        setIsPublishing(false);
        setPublishPulse(".");
        setPublishProgress(0);
      }, 220);
      return;
    }

    setIsPublishing(false);
    setPublishPulse(".");
    setPublishProgress(0);
  }, []);

  useEffect(() => {
    return () => {
      if (publishProgressTimerRef.current) {
        clearInterval(publishProgressTimerRef.current);
      }
      if (publishPulseTimerRef.current) {
        clearInterval(publishPulseTimerRef.current);
      }
    };
  }, []);

  const formatCoordsShort = (lat, lon) => {
    const a = Number(lat);
    const b = Number(lon);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return "";
    const la = a.toFixed(5);
    const lo = b.toFixed(5);
    return `${la}, ${lo}`;
  };

  const pickCityLineFromNominatim = (data) => {
    const addr = data?.address || {};

    const city =
      addr.city ||
      addr.town ||
      addr.village ||
      addr.hamlet ||
      addr.municipality ||
      "";

    const state = addr.state || addr.region || addr.state_district || "";

    const country = addr.country || "";

    const cityClean = String(city || "").trim();
    const stateClean = String(state || "").trim();

    if (
      cityClean &&
      stateClean &&
      cityClean.toLowerCase() !== stateClean.toLowerCase()
    ) {
      return `${cityClean}, ${stateClean}`;
    }
    if (cityClean) return cityClean;
    if (stateClean) return stateClean;

    const county = String(addr.county || "").trim();
    if (county) return county;

    const display = String(data?.display_name || "").trim();
    if (display) {
      const parts = display
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (parts.length >= 2) return `${parts[0]}, ${parts[1]}`;
      return parts[0] || "";
    }

    return country ? String(country).trim() : "";
  };

  const reverseGeocodeNominatim = async (lat, lon) => {
    const url =
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2` +
      `&lat=${encodeURIComponent(lat)}` +
      `&lon=${encodeURIComponent(lon)}` +
      `&zoom=12&addressdetails=1` +
      `&accept-language=uk`;

    const res = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        // Пріоритет української
        "Accept-Language": "uk,uk-UA;q=0.9,en;q=0.4",
      },
    });

    if (!res.ok) {
      throw new Error(`reverse_geocode_http_${res.status}`);
    }

    const data = await res.json();
    const line = pickCityLineFromNominatim(data);
    return line || "";
  };

  const requestAndFillGeolocation = useCallback(async () => {
    if (isPublishing) return;

    setGeoError("");

    if (typeof window === "undefined") return;
    if (!navigator?.geolocation) {
      setGeoError("Геолокація недоступна в цьому браузері.");
      setMessage("Геолокація недоступна в цьому браузері.");
      return;
    }

    setGeoLoading(true);

    const getPosition = () =>
      new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 9000,
          maximumAge: 5 * 60 * 1000,
        });
      });

    try {
      const pos = await getPosition();
      const lat = pos?.coords?.latitude;
      const lon = pos?.coords?.longitude;
      const accuracy = pos?.coords?.accuracy;

      lastGeoRef.current = {
        lat,
        lon,
        accuracy,
        ts: Date.now(),
      };

      let resolved = "";
      try {
        resolved = await reverseGeocodeNominatim(lat, lon);
      } catch (e) {
        resolved = "";
      }

      if (!resolved) {
        const coords = formatCoordsShort(lat, lon);
        resolved = coords ? `📍 ${coords}` : "";
      }

      if (resolved) {
        setLocation(resolved);
        setMessage("Локацію підтягнув.");
      } else {
        setGeoError(
          "Не вдалося визначити локацію. Спробуйте ще раз або введіть вручну."
        );
        setMessage("Не вдалося визначити локацію. Введіть вручну.");
      }
    } catch (err) {
      const code = err?.code;
      let txt = "Не вдалося отримати локацію.";

      if (code === 1) {
        txt =
          "Доступ до геолокації заборонено. Увімкніть його в налаштуваннях браузера.";
      } else if (code === 2) {
        txt = "Локацію не визначено (немає сигналу/служб геолокації).";
      } else if (code === 3) {
        txt = "Запит геолокації перевищив час очікування. Спробуйте ще раз.";
      }

      setGeoError(txt);
      setMessage(txt);
    } finally {
      setGeoLoading(false);
    }
  }, [isPublishing]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, []);

  useEffect(() => {
  if (!isBusinessVacancyMode) return;

  setView("listing");
  setCategory("Карʼєра");
  setIsCategoryManuallySelected(true);
  setTransferType("sale");
  setCondition("na");
}, [isBusinessVacancyMode]);

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!showCategories) return;
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setShowCategories(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setShowCategories(false);
    };
    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [showCategories]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedTitle(title.trim()), 500);
    return () => clearTimeout(timeout);
  }, [title]);

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedDescription(description.trim()),
      500
    );
    return () => clearTimeout(timeout);
  }, [description]);

    const lastAutoCategoryRef = useRef("");

  useEffect(() => {
    if (isBusinessVacancyMode || isCategoryManuallySelected) return;

    const titleCandidate = detectAutoCategory(debouncedTitle);
    const descriptionCandidate = detectAutoCategory(debouncedDescription);

    const nextAutoCategory = titleCandidate || descriptionCandidate || "";

    const noTitle = !debouncedTitle.trim();
    const noDescription = !debouncedDescription.trim();
    const noTextAtAll = noTitle && noDescription;

    if (nextAutoCategory) {
      lastAutoCategoryRef.current = nextAutoCategory;

      setCategory((prev) => {
        if (prev === nextAutoCategory) return prev;
        return nextAutoCategory;
      });

      return;
    }

    if (noTextAtAll) {
      lastAutoCategoryRef.current = "";
      setCategory((prev) => {
        if (!prev) return prev;
        return "";
      });
    }
  }, [
    debouncedTitle,
    debouncedDescription,
    isCategoryManuallySelected,
  ]);

  useEffect(() => {
    if (!userId) return;
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/account/${userId}`);
        setUserProfile(res.data);
        const p =
          res.data?.phone ||
          res.data?.phoneNumber ||
          res.data?.phone_number ||
          "";
        if (p) setContactPhone(p);
        setShowPhone(true);
      } catch (err) {
        console.error("❌ Помилка завантаження профілю:", err);
      }
    };
    fetchProfile();
  }, [userId]);

  useEffect(() => {
    const hasBase = title.trim() !== "" && category !== "" && photos.length > 0;
    let transferValid = false;
    if (transferType === "sale") {
      transferValid = !!priceData.price && !!(priceData.currency || "ГРН");
    } else if (transferType === "exchange") {
      transferValid = !!priceData.exchangeFor;
    } else if (transferType === "free") {
      transferValid = true;
    }
    setIsFormValid(hasBase && transferValid);
  }, [title, category, photos, transferType, priceData]);

  const uploadSingleFile = async (file, onProgress) => {
    const compressed = await compressImage(file, {
      maxSide: 1600,
      mimeType: "image/webp",
      quality: 0.82,
    });
    const fd = new FormData();
    fd.append("photo", compressed, compressed.name);
    const res = await axios.post(`${API_BASE}/api/listings/upload-photo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (!evt.total) return;
        const pct = Math.round((evt.loaded * 100) / evt.total);
        onProgress?.(pct);
      },
    });
    return res.data.photoUrl;
  };

  const handlePhotosSelect = async (e) => {
    if (isPublishing) {
      if (e?.target) e.target.value = "";
      return;
    }

    const fileList = Array.from(e.target.files || []);
    if (fileList.length === 0) return;
    const remaining = maxPhotos - photos.length;
    const toUpload = fileList.slice(0, remaining);
    if (toUpload.length === 0) {
      e.target.value = "";
      return;
    }

    const placeholders = toUpload.map((f) => ({
      url: URL.createObjectURL(f),
      uploading: true,
      progress: 0,
      isLocal: true,
    }));
    setPhotos((prev) => [...prev, ...placeholders]);
    const startIndex = photos.length;

    try {
      setUploading(true);
      await Promise.all(
        toUpload.map(async (file, i) => {
          const index = startIndex + i;
          const setProgress = (pct) => {
            setPhotos((prev) => {
              const arr = [...prev];
              const item = arr[index];
              if (!item) return prev;
              arr[index] = { ...item, progress: pct };
              return arr;
            });
          };
          try {
            const uploadedUrl = await uploadSingleFile(file, setProgress);
            setPhotos((prev) => {
              const arr = [...prev];
              const item = arr[index];
              if (!item) return prev;
              arr[index] = {
                url: uploadedUrl,
                uploading: false,
                progress: 100,
                isLocal: false,
              };
              return arr;
            });
          } catch {
            setPhotos((prev) => {
              const arr = [...prev];
              const item = arr[index];
              if (!item) return prev;
              arr[index] = {
                ...item,
                uploading: false,
                progress: 0,
                error: true,
              };
              return arr;
            });
          }
        })
      );
    } finally {
      setUploading(false);
      if (e?.target) e.target.value = "";
    }
  };

  const triggerFileDialog = () => {
    if (isPublishing) return;
    fileInputRef.current?.click();
  };

  const removePhotoAt = (index) => {
    if (isPublishing) return;
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const dragItemIndex = useRef(null);
  const onDragStart = (index) => (e) => {
    if (isPublishing) return;
    dragItemIndex.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.classList.add("dragging");
  };
  const onDragOver = (index) => (e) => {
    if (isPublishing) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    e.currentTarget.classList.add("drag-over");
  };
  const onDrop = (index) => (e) => {
    if (isPublishing) return;
    e.preventDefault();
    const from = dragItemIndex.current;
    if (from === null || from === index) {
      e.currentTarget.classList.remove("drag-over");
      return;
    }
    setPhotos((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(index, 0, moved);
      return arr;
    });
    dragItemIndex.current = null;
    e.currentTarget.classList.remove("drag-over");
    document
      .querySelectorAll(".photo-grid .tile.filled.dragging")
      .forEach((el) => el.classList.remove("dragging"));
  };

  const setDeal = (type) => {
    if (isPublishing) return;

    if (type === "sale") {
      setTransferType("sale");
      setPriceData((prev) => ({
        ...prev,
        exchangeFor: "",
        currency: prev.currency || "ГРН",
      }));
      return;
    }
    if (type === "exchange") {
      setTransferType("exchange");
      setPriceData((prev) => ({ ...prev, price: "" }));
      setIsNegotiable(false);
      return;
    }
    setTransferType("free");
    setPriceData((prev) => ({ ...prev, price: "", exchangeFor: "" }));
    setIsNegotiable(false);
  };

  const photoUrls = useMemo(
    () =>
      photos
        .filter((p) =>
          typeof p === "string" ? true : !p.uploading && !p.error && p.url
        )
        .map((p) => (typeof p === "string" ? p : p.url)),
    [photos]
  );

  const scrollToAndMark = (ref, className = "field-error") => {
    const node = ref?.current ?? ref;
    if (!node) return;
    node.classList.add(className);
    if (node.scrollIntoView) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const inputEl = node.querySelector?.("input, textarea, select") || node;
    inputEl.focus?.();
    setTimeout(() => node.classList.remove(className), 2400);
  };

  const playfulLines = {
    title: "Заголовок проситься на сцену — дайте йому ім’я.",
    category: "Оберіть категорію — без неї алгоритми сумують.",
    photos: "Додайте хоча б одне фото. Люди вірять очам, а не телепатії",
    price: "Вкажіть суму — навіть якщо символічну.",
    exchange: "А на що міняємо? Напишіть коротко.",
  };

  /* ===== Draft: build / save / load / clear ===== */
  const buildListingDraft = useCallback(() => {
    const uploadedPhotos = photoUrls || [];
    return {
      version: 1,
      kind: "listing",
      updatedAt: nowIso(),
      title: title || "",
      description: description || "",
      category: category || "",
      isCategoryManuallySelected: !!isCategoryManuallySelected,
      photos: uploadedPhotos,
      photosCount: uploadedPhotos.length,
      formData: { attributes: formData?.attributes || {} },
      transferType: transferType || "sale",
      priceData: priceData || { currency: "ГРН" },
      location: location || "",
      showPhone: !!showPhone,
      contactPhone: (contactPhone || "").trim(),
      condition: condition || "na",
      isNegotiable: !!isNegotiable,
    };
  }, [
    title,
    description,
    category,
    isCategoryManuallySelected,
    photoUrls,
    formData,
    transferType,
    priceData,
    location,
    showPhone,
    contactPhone,
    condition,
    isNegotiable,
  ]);

  const draftLooksMeaningful = useCallback((d) => {
    if (!d) return false;
    const t = (d.title || "").trim();
    const desc = (d.description || "").trim();
    const hasPhotos = Array.isArray(d.photos) && d.photos.length > 0;
    const hasCat = !!(d.category || "");
    const hasAttrs =
      d.formData &&
      d.formData.attributes &&
      Object.keys(d.formData.attributes).length > 0;
    const hasPrice =
      d.priceData && (d.priceData.price || d.priceData.exchangeFor);
    const hasLocation = !!(d.location || "").trim();
    return !!(
      t ||
      desc ||
      hasPhotos ||
      hasCat ||
      hasAttrs ||
      hasPrice ||
      hasLocation
    );
  }, []);

  const clearListingDraft = useCallback(() => {
    try {
      localStorage.removeItem(LISTING_DRAFT_LS_KEY);
    } catch {}
  }, []);

  const hydrateFromDraft = useCallback((d) => {
    if (!d) return;

    suppressDraftWriteRef.current = true;

    setTitle(d.title || "");
    setDescription(d.description || "");
    setCategory(d.category || "");
    setIsCategoryManuallySelected(!!d.isCategoryManuallySelected);

    const restoredPhotos = Array.isArray(d.photos) ? d.photos : [];
    setPhotos(restoredPhotos);

    setFormData(d.formData || { attributes: {} });

    setTransferType(d.transferType || "sale");
    setPriceData(d.priceData || { currency: "ГРН" });

    setLocation(d.location || "");
    setShowPhone(typeof d.showPhone === "boolean" ? d.showPhone : true);
    setContactPhone((d.contactPhone || "").trim());

    setCondition(d.condition || "na");

    setIsNegotiable(!!d.isNegotiable);

    requestAnimationFrame(() => {
      suppressDraftWriteRef.current = false;
    });
  }, []);

  useEffect(() => {
    if (draftHydratedRef.current) return;
    draftHydratedRef.current = true;

    try {
      const raw = localStorage.getItem(LISTING_DRAFT_LS_KEY);
      const parsed = safeJsonParse(raw || "");
      if (parsed && parsed.kind === "listing" && draftLooksMeaningful(parsed)) {
        setDraftCandidate(parsed);
        setShowDraftPrompt(true);
      }
    } catch {}
  }, [draftLooksMeaningful]);

  const draftWriteTimerRef = useRef(null);
  useEffect(() => {
    if (suppressDraftWriteRef.current) return;

    const draft = buildListingDraft();
    if (!draftLooksMeaningful(draft)) {
      clearTimeout(draftWriteTimerRef.current);
      draftWriteTimerRef.current = setTimeout(() => {
        clearListingDraft();
      }, 450);
      return;
    }

    clearTimeout(draftWriteTimerRef.current);
    draftWriteTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(LISTING_DRAFT_LS_KEY, JSON.stringify(draft));
      } catch {}
    }, 500);

    return () => clearTimeout(draftWriteTimerRef.current);
  }, [buildListingDraft, draftLooksMeaningful, clearListingDraft]);

  const resetListingForm = useCallback(() => {
    setTitle("");
    setDescription("");
    setCategory("");
    setPhotos([]);
    setFormData({ attributes: {} });
    setTransferType("sale");
    setPriceData({ currency: "ГРН" });
    setLocation("");
    setIsCategoryManuallySelected(false);
    setShowPhone(true);
    setContactPhone(
      userProfile?.phone ||
        userProfile?.phoneNumber ||
        userProfile?.phone_number ||
        ""
    );
    setCondition("na");

    setIsNegotiable(false);
    setGeoError("");
  }, [userProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (uploading || isPublishing) return;
        setSubmitError("");

    if (!title.trim()) {
      setMessage(playfulLines.title);
      setSubmitError(playfulLines.title);
      scrollToAndMark(titleRef);
      return;
    }
    if (!category) {
      setMessage(playfulLines.category);
      setSubmitError(playfulLines.category);
      scrollToAndMark(categoryTriggerRef);
      return;
    }
    if (photos.length === 0) {
      setMessage(playfulLines.photos);
      setSubmitError(playfulLines.photos);
      scrollToAndMark(photosGridRef, "block-error");
      return;
    }
    if (transferType === "sale" && !priceData.price) {
      setMessage(playfulLines.price);
      setSubmitError(playfulLines.price);
      scrollToAndMark(priceRef);
      return;
    }
    if (transferType === "exchange" && !priceData.exchangeFor) {
      setMessage(playfulLines.exchange);
      setSubmitError(playfulLines.exchange);
      scrollToAndMark(exchangeRef);
      return;
    }

    if (!userId) return;

    const attributes = collectAttributes(category, formData.attributes || {});
    const payload = {
      userId,
      title,
      description,
      category: isBusinessVacancyMode ? "Карʼєра" : category,
      photos: photoUrls,
      price: transferType === "sale" ? parseFloat(priceData.price || 0) : 0,
      type: isBusinessVacancyMode ? "vacancy" : transferType || "sale",
      currency:
        transferType === "sale"
          ? priceData.currency || "ГРН"
          : priceData.currency || "",
      exchangeFor: isBusinessVacancyMode ? "" : priceData.exchangeFor || "",
      location: location || "",
      attributes: {
        ...attributes,
        ...(isBusinessVacancyMode
          ? {
              entityType: "business_vacancy",
              moduleKey: "vacancies",
              businessOwnerId: userId,
              feedVisible: true,
            }
          : {}),
      },
      entityType: isBusinessVacancyMode ? "business_vacancy" : undefined,
      moduleKey: isBusinessVacancyMode ? "vacancies" : undefined,
      businessOwnerId: isBusinessVacancyMode ? userId : undefined,
      feedVisible: isBusinessVacancyMode ? true : undefined,
      showPhone: !!showPhone,
      contactPhone: (contactPhone || "").trim(),
      condition: "na",
      isNegotiable: false,
    };

    setSubmitError("");
    startPublishingVisualState();
    setMessage("Публікую оголошення, зачекайте" + publishPulse);

    try {
      const token = await getAccessToken();

      if (!token) {
        stopPublishingVisualState(false);
        setMessage("Сесія недійсна. Увійдіть повторно.");
        setSubmitError("Сесія недійсна. Увійдіть повторно.");
        return;
      }

      await axios.post(`${API_BASE}/api/listings`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      stopPublishingVisualState(true);
      setMessage("Оголошення створено!");
      setSubmitError("");
      setShowSuccess(true);

      clearListingDraft();
      resetListingForm();
    } catch (err) {
      console.error("❌ Помилка створення оголошення:", err);
      stopPublishingVisualState(false);

      const backendError =
      err.response?.data?.message ||
      err.response?.data?.error ||
      "Не вдалося створити оголошення";

    setMessage(backendError);
    setSubmitError(backendError);
    }
  };

  useEffect(() => {
    if (!isPublishing) return;
    setMessage("Публікую оголошення, зачекайте" + publishPulse);
  }, [publishPulse, isPublishing]);

  const itemIsUploading = (item) =>
    typeof item === "string" ? false : !!item.uploading;
  const itemProgress = (item) =>
    typeof item === "string" ? 100 : item.progress ?? 0;
  const itemUrl = (item) => (typeof item === "string" ? item : item?.url);

  return (
    <>
      <DynamicHeader
        title={
          view === "listing"
            ? "Створити оголошення"
            : view === "search"
            ? "Створити запит"
            : "Створити допис"
        }
        variant="glass"
        size="compact"
        hideOnScroll={true}
        showBack={true}
        showMenu={false}
      />
      <div className="local-header-spacer small" />

      <DraftResumePrompt
        open={showDraftPrompt && view === "listing"}
        title="Є незавершене оголошення"
        subtitle="Схоже, ви вже починали створювати оголошення. Продовжимо з того місця чи почнемо спочатку?"
        meta=""
        onResume={() => {
          if (isPublishing) return;
          hydrateFromDraft(draftCandidate);
          setShowDraftPrompt(false);
          setDraftCandidate(null);
          setMessage("Відновив чернетку. Давайте закінчимо.");
        }}
        onDiscard={() => {
          if (isPublishing) return;
          clearListingDraft();
          setShowDraftPrompt(false);
          setDraftCandidate(null);
          resetListingForm();
          setMessage("Ок, почнемо спочатку.");
        }}
        onClose={() => {
          if (isPublishing) return;
          setShowDraftPrompt(false);
        }}
      />

      <div
        className={`create-listing ios-glass-card ${mounted ? "enter" : ""} ${
          isPublishing ? "is-publishing" : ""
        }`}
        role="region"
        aria-label="Форма створення"
        aria-busy={isPublishing}
        style={{
          position: "relative",
          overflow: "hidden",
        }}
      >
        {isPublishing && (
          <div
            className="publish-lock-overlay"
            aria-live="polite"
            aria-label="Публікація оголошення"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
              background:
                "linear-gradient(180deg, rgba(15,23,42,0.48) 0%, rgba(15,23,42,0.68) 100%)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 420,
                borderRadius: 24,
                padding: "22px 18px",
                background: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.24)",
                boxShadow: "0 10px 40px rgba(0,0,0,0.18)",
                textAlign: "center",
                color: "#fff",
              }}
            >
              <div
                style={{
                  width: 58,
                  height: 58,
                  margin: "0 auto 16px",
                  borderRadius: "50%",
                  border: "3px solid rgba(255,255,255,0.22)",
                  borderTopColor: "#ffffff",
                  animation: "cvantoSpin 0.9s linear infinite",
                }}
              />
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  marginBottom: 8,
                  letterSpacing: "0.01em",
                }}
              >
                Зачекайте, публікуємо
              </div>
              <div
                style={{
                  fontSize: 14,
                  lineHeight: 1.45,
                  opacity: 0.92,
                  marginBottom: 16,
                }}
              >
                Не натискайте повторно. Поки триває відправка, форма тимчасово
                заблокована.
              </div>

              <div
                style={{
                  width: "100%",
                  height: 10,
                  borderRadius: 999,
                  overflow: "hidden",
                  background: "rgba(255,255,255,0.14)",
                  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.12)",
                }}
              >
                <div
                  style={{
                    width: `${Math.max(8, publishProgress)}%`,
                    height: "100%",
                    borderRadius: 999,
                    background:
                      "linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.72) 100%)",
                    transition: "width 220ms ease",
                  }}
                />
              </div>

              <div
                style={{
                  marginTop: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  opacity: 0.95,
                }}
              >
                {publishProgress}% 
              </div>
            </div>

            <style>
              {`
                @keyframes cvantoSpin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        )}

        <div
          className="switch-container as-segmented glassy"
          style={{
            pointerEvents: isPublishing ? "none" : "auto",
            opacity: isPublishing ? 0.72 : 1,
          }}
        >
          <div
            className={`switch-btn ${view === "listing" ? "active" : ""}`}
            onClick={() => !isPublishing && setView("listing")}
            role="button"
            tabIndex={isPublishing ? -1 : 0}
            aria-pressed={view === "listing"}
            onKeyDown={(e) =>
              !isPublishing &&
              (e.key === "Enter" || e.key === " ") &&
              setView("listing")
            }
          >
            <img src={listingIcon} alt="" />
            <span>Оголошення</span>
          </div>

          <div
            className={`switch-btn ${view === "search" ? "active" : ""}`}
            onClick={() => !isPublishing && setView("search")}
            role="button"
            tabIndex={isPublishing ? -1 : 0}
            aria-pressed={view === "search"}
            onKeyDown={(e) =>
              !isPublishing &&
              (e.key === "Enter" || e.key === " ") &&
              setView("search")
            }
          >
            <img src={searchIcon} alt="" />
            <span>Шукаю</span>
          </div>

          <div
            className={`switch-btn ${view === "post" ? "active" : ""}`}
            onClick={() => !isPublishing && setView("post")}
            role="button"
            tabIndex={isPublishing ? -1 : 0}
            aria-pressed={view === "post"}
            onKeyDown={(e) =>
              !isPublishing &&
              (e.key === "Enter" || e.key === " ") &&
              setView("post")
            }
          >
            <img src={postIcon} alt="" />
            <span>Допис</span>
          </div>
          <div className={`highlight ${view}`} aria-hidden="true"></div>
        </div>

        {message && <p className="message">{message}</p>}

        {view === "listing" ? (
          <>
            <h2>Створити оголошення</h2>

            <form
              onSubmit={handleSubmit}
              className="create-listing-form"
              noValidate
              style={{
                pointerEvents: isPublishing ? "none" : "auto",
                opacity: isPublishing ? 0.76 : 1,
                transition: "opacity 0.18s ease",
              }}
            >
              <h3>Деталі оголошення</h3>

              <input
                ref={titleRef}
                type="text"
                className="pill-50"
                placeholder="Заголовок *"
                value={title}
                onChange={(e) => {
                if (isPublishing) return;
                setTitle(e.target.value);
              }}
                aria-required="true"
                disabled={isBusy}
              />

              <textarea
                className="pill-50"
                placeholder="Опис"
                value={description}
                onChange={(e) => {
                if (isPublishing) return;
                setDescription(e.target.value);
              }}
                disabled={isBusy}
              />

              <div className="custom-select-wrapper" ref={selectRef}>
                <div
                  ref={categoryTriggerRef}
                  className={`custom-select-trigger pill-50 ${
                      showCategories ? "open" : ""
                    } ${isBusy ? "disabled" : ""} ${
                      isBusinessVacancyMode ? "is-locked" : ""
                    }`}
                    onClick={() => {
                      if (isBusy || isBusinessVacancyMode) return;
                      setShowCategories(!showCategories);
                    }}
                    role="button"
                    tabIndex={isBusy || isBusinessVacancyMode ? -1 : 0}
                    onKeyDown={(e) => {
                      if (isBusy || isBusinessVacancyMode) return;

                      if (e.key === "Enter" || e.key === " ") {
                        setShowCategories(!showCategories);
                      }
                    }}
                  aria-haspopup="listbox"
                  aria-expanded={showCategories}
                  aria-controls="category-dropdown"
                >
                  {category ? (
                    <div className="selected-category">
                      <div
                        className="category-icon"
                        style={{
                          backgroundImage: `url(${categoryVisualMap[category]})`,
                        }}
                      />
                      <span>{category}</span>
                    </div>
                  ) : (
                    <span className="placeholder">Оберіть категорію *</span>
                  )}
                  <span className="chevron" aria-hidden="true">
                    ▾
                  </span>
                </div>

                <div
                  id="category-dropdown"
                  className={`custom-select-dropdown ${
                    showCategories ? "open" : "closed"
                  }`}
                  role="listbox"
                  aria-hidden={!showCategories}
                >
                  {Object.entries(categoryVisualMap).map(([name, image], idx) => (
                    <div
                      key={name}
                      className="dropdown-item"
                      style={{ "--item-index": idx }}
                      onClick={() => {
                        if (isBusy || isBusinessVacancyMode) return;
                        setCategory(name);
                        setIsCategoryManuallySelected(true);
                        setShowCategories(false);
                      }}
                      role="option"
                      aria-selected={category === name}
                      tabIndex={isBusy ? -1 : 0}
                      onKeyDown={(e) => {
                        if (isBusy) return;
                        if (e.key === "Enter" || e.key === " ") {
                          setCategory(name);
                          setIsCategoryManuallySelected(true);
                          setShowCategories(false);
                        }
                      }}
                    >
                      <div
                        className="category-icon"
                        style={{ backgroundImage: `url(${image})` }}
                      />
                      <span>{name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="photo-upload">
                <label>Фото</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotosSelect}
                  style={{ display: "none" }}
                  disabled={isBusy}
                />
              </div>

              <div
                className="photo-grid"
                aria-label="Галерея фото"
                ref={photosGridRef}
              >
                {photos.map((item, idx) => {
                  const url = itemUrl(item);
                  const isUploading = itemIsUploading(item);
                  const progress = itemProgress(item);

                  return (
                    <div
                      key={`tile-${idx}`}
                      className={`tile filled ${
                        isUploading ? "is-uploading" : ""
                      }`}
                      draggable={!isUploading && !isPublishing}
                      onDragStart={
                        !isUploading && !isPublishing
                          ? onDragStart(idx)
                          : undefined
                      }
                      onDragOver={
                        !isUploading && !isPublishing
                          ? onDragOver(idx)
                          : undefined
                      }
                      onDrop={
                        !isUploading && !isPublishing
                          ? onDrop(idx)
                          : undefined
                      }
                    >
                      <img src={url} alt={`Фото ${idx + 1}`} />
                      {idx === 0 && !isUploading && (
                        <span className="main-badge">Головне</span>
                      )}

                      {isUploading && (
                        <div
                          className="progress-overlay"
                          aria-label="Завантаження фото"
                        >
                          <div className="ring">
                            <svg
                              viewBox="0 0 36 36"
                              width="64"
                              height="64"
                              aria-hidden="true"
                            >
                              <path
                                className="bg"
                                d="M18 2.0845
                                   a 15.9155 15.9155 0 0 1 0 31.831
                                   a 15.9155 15.9155 0 0 1 0 -31.831"
                              />
                              <path
                                className="meter"
                                strokeDasharray={`${
                                  Math.max(1, Math.min(100, progress))
                                }, 100`}
                                d="M18 2.0845
                                   a 15.9155 15.9155 0 0 1 0 31.831
                                   a 15.9155 15.9155 0 0 1 0 -31.831"
                              />
                            </svg>
                            <div className="pct">{progress}%</div>
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removePhotoAt(idx)}
                        aria-label="Видалити фото"
                        title="Видалити фото"
                        disabled={isUploading || isPublishing}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}

                {photos.length < maxPhotos && (
                  <button
                    type="button"
                    className="tile empty add-more"
                    onClick={triggerFileDialog}
                    disabled={isBusy}
                    aria-label="Додати фото"
                    title="Додати фото"
                  >
                    <img
                      src={addImageIcon}
                      alt="Додати фото"
                      className="add-image-icon"
                    />
                  </button>
                )}
              </div>

              {category && (
                <CategoryFields
                  category={category}
                  formData={formData}
                  setFormData={setFormData}
                />
              )}

              {isConditionRelevant(category) && (
                <div className="condition-block">
                  <h3>Стан</h3>
                  <div className="toggle-buttons segmented glassy">
                    <button
                      type="button"
                      className={condition === "new" ? "active" : ""}
                      onClick={() => !isPublishing && setCondition("new")}
                      disabled={isPublishing}
                    >
                      Нове
                    </button>
                    <button
                      type="button"
                      className={condition === "used" ? "active" : ""}
                      onClick={() => !isPublishing && setCondition("used")}
                      disabled={isPublishing}
                    >
                      Вживане
                    </button>
                    <button
                      type="button"
                      className={condition === "na" ? "active" : ""}
                      onClick={() => !isPublishing && setCondition("na")}
                      title="Коли «стан» не має сенсу"
                      disabled={isPublishing}
                    >
                      Інше
                    </button>
                  </div>
                </div>
              )}

              <div className="transfer-type-toggle">
                <h3>Умови пропозиції</h3>
                <div className="toggle-buttons segmented glassy">
                  <button
                    type="button"
                    className={transferType === "sale" ? "active" : ""}
                    onClick={() => setDeal("sale")}
                    disabled={isPublishing}
                  >
                    Платно
                  </button>
                  <button
                    type="button"
                    className={transferType === "exchange" ? "active" : ""}
                    onClick={() => setDeal("exchange")}
                    disabled={isPublishing}
                  >
                    Обмін
                  </button>
                  <button
                    type="button"
                    className={transferType === "free" ? "active" : ""}
                    onClick={() => setDeal("free")}
                    disabled={isPublishing}
                  >
                    Безкоштовно
                  </button>
                </div>

                {transferType === "sale" && (
                  <>
                    <div className="price-fields">
                      <input
                        ref={priceRef}
                        type="number"
                        className="pill-50"
                        placeholder="Сума *"
                        value={priceData.price || ""}
                        onChange={(e) =>
                          !isPublishing &&
                          setPriceData({ ...priceData, price: e.target.value })
                        }
                        disabled={isBusy}
                      />
                      <select
                        value={priceData.currency || "ГРН"}
                        onChange={(e) =>
                          !isPublishing &&
                          setPriceData({
                            ...priceData,
                            currency: e.target.value,
                          })
                        }
                        aria-label="Валюта"
                        disabled={isBusy}
                      >
                        <option value="">Валюта</option>
                        <option value="ГРН">₴</option>
                        <option value="USD">$</option>
                        <option value="EUR">€</option>
                      </select>
                    </div>

                    {/*Можливий торг */}
                    <div className="negotiable-block ios-glass-block">
                      <div className="toggle-row">
                        <div className="toggle-text">
                          <div className="title">Можливий торг</div>
                          <div
                            className={`state ${isNegotiable ? "on" : "off"}`}
                          >
                            {isNegotiable ? "УВІМКНЕНО" : "ВИМКНЕНО"}
                          </div>
                        </div>

                        <label
                          className={`pretty-switch ${
                            isNegotiable ? "active" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isNegotiable}
                            onChange={(e) =>
                              !isPublishing &&
                              setIsNegotiable(e.target.checked)
                            }
                            aria-label="Можливий торг"
                            disabled={isBusy}
                          />
                          <span className="track">
                            <span className="thumb" />
                          </span>
                        </label>
                      </div>
                    </div>
                  </>
                )}

                {transferType === "exchange" && (
                  <input
                    ref={exchangeRef}
                    type="text"
                    className="pill-50"
                    placeholder="На що би хотіли поміняти? (опц)"
                    value={priceData.exchangeFor || ""}
                    onFocus={() => {
                      if (isPublishing) return;
                      const cur = String(priceData.exchangeFor || "");
                      if (!cur.trim())
                        setPriceData({ ...priceData, exchangeFor: "на " });
                    }}
                    onChange={(e) => {
                      if (isPublishing) return;
                      let v = String(e.target.value || "");

                      v = v.replace(/^\s+/, "");

                      if (!/^на\s*/i.test(v)) {
                        v = "на " + v;
                      } else {
                        v = v.replace(/^на\s*/i, "на ");
                      }

                      setPriceData({ ...priceData, exchangeFor: v });
                    }}
                    disabled={isBusy}
                  />
                )}

                {transferType === "free" && (
                  <p className="free-note">
                    Люди люблять безкоштовне — ви молодець!
                  </p>
                )}
              </div>

              <h3>Контактна інформація</h3>

              {/* Location + geolocation button (added) */}
              <div
                className="ios-glass-block"
                style={{ padding: 0, background: "transparent", border: "none" }}
              >
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <input
                    type="text"
                    className="pill-50"
                    placeholder="Локація"
                    value={location}
                    onChange={(e) =>
                      !isPublishing && setLocation(e.target.value)
                    }
                    style={{ flex: 1 }}
                    disabled={isBusy}
                  />

                  <button
                    type="button"
                    onClick={requestAndFillGeolocation}
                    disabled={geoLoading || isBusy}
                    className="pill-50"
                    style={{
                      width: 210,
                      cursor: geoLoading || isBusy ? "default" : "pointer",
                      opacity: geoLoading || isBusy ? 0.85 : 1,
                    }}
                    aria-label="Підтягнути мою локацію"
                    title="Підтягнути мою локацію"
                  >
                    {geoLoading ? "Підтягую..." : "Підтягнути локацію"}
                  </button>
                </div>

                {geoError ? (
                  <div className="message" style={{ marginTop: 10 }}>
                    {geoError}
                  </div>
                ) : null}
              </div>

              <div className="phone-visibility ios-glass-block">
                <div className="toggle-row">
                  <div className="toggle-text">
                    <div className="title">Показувати номер у оголошенні</div>
                    <div className={`state ${showPhone ? "on" : "off"}`}>
                      {showPhone ? "УВІМКНЕНО" : "ВИМКНЕНО"}
                    </div>
                  </div>

                  <label className={`pretty-switch ${showPhone ? "active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={showPhone}
                      onChange={(e) =>
                        !isPublishing && setShowPhone(e.target.checked)
                      }
                      aria-label="Показувати номер у оголошенні"
                      disabled={isBusy}
                    />
                    <span className="track">
                      <span className="thumb" />
                    </span>
                  </label>
                </div>

                {showPhone && (
                  <input
                    type="tel"
                    className="pill-input"
                    placeholder="Номер телефону"
                    value={contactPhone}
                    onChange={(e) =>
                      !isPublishing && setContactPhone(e.target.value)
                    }
                    disabled={isBusy}
                  />
                )}
              </div>

              {userProfile && (
                <div className="author-info ios-glass-block">
                  <img
                    src={userProfile.avatar}
                    alt="Аватар"
                    className="avatar"
                  />
                  <div className="author-details">
                    <p>{userProfile.username}</p>
                    <p>{userProfile.phone || "Телефон не вказано"}</p>
                  </div>
                </div>
              )}

              {submitError && !isPublishing && (
                <div
                  className="submit-error-banner"
                  role="alert"
                  aria-live="assertive"
                >
                  {submitError}
                </div>
              )}

              <div className="cta-sticky">
                <button
                  type="submit"
                  disabled={isBusy}
                  className="primary-cta with-sheen"
                  aria-busy={isPublishing}
                >
                  {uploading
                    ? "Завантаження фото..."
                    : isPublishing
                    ? `Публікую${publishPulse}`
                    : "Створити оголошення"}
                </button>
              </div>

              <div className="bottom-safe-space" aria-hidden="true" />
            </form>
          </>
        ) : view === "search" ? (
          <CreateSearch embedded />
        ) : (
          <CreatePost embedded />
        )}
      </div>

      <SuccessModal
        open={showSuccess}
        title="Опубліковано"
        subtitle="Оголошення додано у стрічку"
        onComplete={() => navigate("/feed")}
      />
    </>
  );
};

export default CreateListing;